import React, { useState, useEffect, useMemo,useRef } from "react";
import { FaSearch, FaCaretDown, FaRedoAlt } from "react-icons/fa";
import Sidebar from "../sidebar/Sidebar";
import apiService from "../../utils/api";
import downloadIcon from '../../assets/download.svg';
import linktofileIcon from '../../assets/linktofile.svg';
import arrow from '../../assets/arrow.svg';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import RestoreConfirmationModal from '../modals/RestoreConfirmationModal';
import "./TrashPage.css";
import RestoreIcon from '@mui/icons-material/Restore';
import Breadcrumbs from "../../components/Breadcrumbs";
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { Info } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from 'primereact/progressspinner';
import { Delete } from '@mui/icons-material';
import Loader from '../Loader';
import Snackbar from '@mui/material/Snackbar';
import RestoreEmailTemplate from '../../utils/RestoreEmailTemplate';
const getUserId = () => {
  const storedUserId = sessionStorage.getItem('user_id');
  return storedUserId || ""; 
};
const getCleanContractId = (id) => {
  // Safe bounded regex: limits digits, anchored, and Unicode-safe
  const match = String(id).match(/\d{1,20}$/u);
  return match ? match[0] : id;
};

const getClientKey = (nodeKey) => {
  const parts = nodeKey.split("-");
  return parts[0] + "-" + parts[1]; // e.g., "CLIENT-3-TYPE" → adapts to client-type
};

// For flat trash data – assign keys and empty children
const addLevels = (nodes, level = 0) => {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node, index) => {
    // Clone node to avoid mutating original
    const newNode = { ...node, level };
    // For files: Use contract_title as name, ensure file_url
    if (newNode.data?.type === "File") {
      newNode.data = {
        ...newNode.data,
        name: newNode.data.contract_title || newNode.data.name || "Unnamed Contract",
        file_url: newNode.data.url || `/files/${newNode.data.contract_id}`,
      };
    }
    // Recurse on children
    if (newNode.children && newNode.children.length > 0) {
      newNode.children = addLevels(newNode.children, level + 1);
    } else {
      newNode.children = []; // Ensure empty array for leaves
    }
    return newNode;
  });
};

const calculateModified = (trashedAt) => {
  try {
    const now = new Date();
    const trashedDate = new Date(trashedAt.replace(/\//g, '-')); 
    if (isNaN(trashedDate.getTime())) {
      return 'Unknown';
    }
    const diffMs = now - trashedDate;
    const absDiffMs = Math.abs(diffMs);
    const diffHours = Math.floor(absDiffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(absDiffMs / (1000 * 60 * 60 * 24 * 30));
    const suffix = diffMs >= 0 ? "ago" : "from now";

    if (absDiffMs < 1000 * 60) return "just now";
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ${suffix}`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? "s" : ""} ${suffix}`;
    return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ${suffix}`;
  } catch (error) {
    console.error("Error calculating modified time:", error);
    return 'Unknown';
  }
};

const statusColorMap = {
  'Pending Approval': '#F59E0B',
  'Approved': '#10B981',
  'Archived': '#6B7280',
  'Trashed': '#ff6b35',
  'Moved to Trash': '#ff6b35',
};

const tagColorMap = {
  'Statement of Work(SOW)': '#78DAC8',
  'Change Request(CR)': '#D6A3FF',
  'Vendor Agreement': '#FFB27D',
  'Master Services Agreement(MSA)': '#3B82F6',
};

const TrashPage = () => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [client, setClient] = useState("");
  const [days, setDays] = useState("");
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [rawContracts, setRawContracts] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, success: false, message: '' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [contractToAction, setContractToAction] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [userName, setUserName] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const navigate = useNavigate();
  const [nodes, setNodes] = useState([]);
   const [snackbarOpen, setSnackbarOpen] = useState(false); // New state for open/close
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const downloadRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ key: 'dateCreated', direction: 'desc' });
  const mandatoryColumns = ['name', 'status', 'dateCreated'];
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedColumns = localStorage.getItem('trashTableVisibleColumns');
    const initialColumns = {
      name: true, 
      clientName: true,
      dateCreated: true,
      status: true,
      modified: true,
      actions: true,
    };
    let cols = savedColumns ? JSON.parse(savedColumns) : initialColumns;
  mandatoryColumns.forEach(col => {
    if (cols.hasOwnProperty(col)) cols[col] = true;
  });
  return cols;
  });
  const [tempVisibleColumns, setTempVisibleColumns] = useState({ ...visibleColumns });
  const [loadingCount, setLoadingCount] = useState(0);

  const userId = getUserId();

  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const stopLoading = () => setLoadingCount((prev) => Math.max(0, prev - 1));
  const isLoading = loadingCount > 0;
  const [originalHierarchy, setOriginalHierarchy] = useState([]);          
const [currentHierarchyMode, setCurrentHierarchyMode] = useState("default"); 
const [switchingHierarchy, setSwitchingHierarchy] = useState(false);     

  useEffect(() => {
  if (showColumnOptions) {
    let temp = { ...visibleColumns };
    mandatoryColumns.forEach(col => {
      if (temp.hasOwnProperty(col)) temp[col] = true;
    });
    setTempVisibleColumns(temp);
  }
}, [showColumnOptions, visibleColumns]);

  useEffect(() => {
    localStorage.setItem('trashTableVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      startLoading();
      try {
        const statusData = await apiService.fetchData("metadata/contract-status");
        const contractTypeData = await apiService.fetchData("metadata/contract-type");
        setStatusOptions(statusData || []);
        setTypeOptions(contractTypeData || []);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        setSnackbar({ open: true, success: false, message: `Failed to load dropdown data: ${error.message}` });
      } finally {
        stopLoading();
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const storedName = sessionStorage.getItem('user_name') || 'Unknown User';
    const storedEmail = sessionStorage.getItem('user_emailId') || 'unknown@email.com';
    setUserName(storedName);
    setUserEmail(storedEmail);
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      startLoading();
      try {
        const clientData = await apiService.fetchData("ruddr/client");
        if (clientData?.data) {
          const mappedClients = clientData.data.map((c) => ({
            id: c.client_id,
            label: c.name || c.client_email || `Client ${c.client_id}`,
            email: c.client_email,
          }));
          setClientOptions(mappedClients);
        } else {
          throw new Error("Invalid client data response");
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
        setSnackbar({ open: true, success: false, message: `Failed to load clients: ${error.message}` });
      } finally {
        stopLoading();
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
  const fetchTrash = async () => {
    startLoading();
    try {
      if (!userId) {
        setSnackbar({ open: true, success: false, message: "User ID not found" });
        return;
      }
      const queryParams = `deleted-contracts?user_id=${userId}`;
      console.log('Fetching deleted contracts with URL:', queryParams);
      const response = await apiService.fetchData(queryParams);
      const hierarchy = response?.contract_hierarchy || [];
      setOriginalHierarchy(hierarchy);
      const withLevels = addLevels(hierarchy);
      setNodes(withLevels);
      console.log("deleted contracts hierarchy with levels:", withLevels);

      if (withLevels.length === 0) {
        setSnackbar({ open: true, success: false, message: "No deleted contracts found" });
      }
    } catch (error) {
      console.error("Error fetching deleted contracts:", error);
      setSnackbar({ open: true, success: false, message: `Failed to load deleted documents: ${error.message}` });
      setNodes([]);
    } finally {
      stopLoading();
    }
  };

  fetchTrash();
}, [userId]);

// 🔹 Flatten hierarchical nodes into a flat array of file objects
const flattenNodes = (nodeList) => {
  let flat = [];
  nodeList.forEach(node => {
    if (node.data?.type === 'File') flat.push(node.data);
    if (node.children?.length) flat = flat.concat(flattenNodes(node.children));
  });
  return flat;
};

// 🔹 Build a flat file list
const allFiles = flattenNodes(nodes);

// 🔹 Apply search filtering
const filteredFiles = allFiles.filter(file =>
  file.name?.toLowerCase().includes(search.toLowerCase()) ||
  file.clientName?.toLowerCase().includes(search.toLowerCase()) ||
  file.status?.toLowerCase().includes(search.toLowerCase())
);
// 🔹 Filtered hierarchical view for TreeTable (so search affects display)
const filterTree = (nodeList) => {
  return nodeList
    .map(node => {
      // If it's a file, check if it matches search
      if (node.data?.type === 'File') {
        const match =
          node.data.name?.toLowerCase().includes(search.toLowerCase()) ||
          node.data.clientName?.toLowerCase().includes(search.toLowerCase()) ||
          node.data.status?.toLowerCase().includes(search.toLowerCase());
        return match ? node : null;
      }

      // If it's a folder, filter its children recursively
      const filteredChildren = filterTree(node.children || []);
      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }

      // Otherwise, exclude this folder
      return null;
    })
    .filter(Boolean);
};

const filteredNodes = search.trim() ? filterTree(nodes) : nodes;


  useEffect(() => {
    if (rawContracts.length > 0) {
      const transformedContracts = rawContracts.map((c, index) => ({
        id: c.contract_id || `cont_${index + 1}`,
        name: c.contract_title || 'Unnamed Contract',
        tagFull: c.contract_type || 'Unknown',
        tag: c.contract_type 
          ? (c.contract_type.split("(")[1]?.replace(")", "") || c.contract_type) 
          : "Unknown",
        tagColor: c.contract_type ? (tagColorMap[c.contract_type] || '#6B7280') : '#6B7280',
        clientName: c.client_email || 'Unknown',
        dateCreated: c.created_at ? new Date(c.created_at.replace(/\//g, '-')).toLocaleDateString('en-GB') : 'N/A',
        rawDate: c.created_at ? new Date(c.created_at.replace(/\//g, '-')) : null,
        status: c.status || 'Trashed',
        statusColor: c.status ? (statusColorMap[c.status] || '#ff6b35') : '#ff6b35',
        modified: calculateModified(c.trashed_at || c.updated_at) || 'N/A',
        rawModified: (c.trashed_at || c.updated_at) ? new Date((c.trashed_at || c.updated_at).replace(/\//g, '-')) : null,
        file_url: c.sharepointPath 
          ? `/files/${c.sharepointPath}` 
          : `/files/${c.contract_id}`,
        reason: c.reason || 'Not specified',
        restoreUntil: c.restore_until || null,
      }));

      const updatedContracts = transformedContracts.map((contract) => {
        const matchingClient = clientOptions.find((opt) => opt.email === contract.clientName);
        return {
          ...contract,
          clientName: matchingClient ? matchingClient.label : contract.clientName,
        };
      });

      setContracts(updatedContracts);
    } else {
      setContracts([]);
    }
  }, [rawContracts, clientOptions]);

  useEffect(() => {
    let timer;
    if (snackbar.open) {
      timer = setTimeout(() => {
        setSnackbar({ ...snackbar, open: false });
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [snackbar.open]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key && prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleRestore = (contract) => {
    setContractToAction(contract);
    setIsRestoreModalOpen(true);
  };

  const handlePermanentDelete = (contract) => {
    setContractToAction(contract);
    setIsDeleteModalOpen(true);
  };

  const swapYearAndClient = (yearFirstNodes) => {
  const clientMap = new Map();

  yearFirstNodes.forEach((yearNode) => {
    const yearKey = yearNode.key;
    const yearName = yearNode.data.year || yearNode.data.name || "Unknown Year";

    (yearNode.children || []).forEach((clientNode) => {
      const clientName = clientNode.data.clientName || clientNode.data.name || "Unknown Client";
      const clientKey = clientNode.key.split("-")[0];

      if (!clientMap.has(clientKey)) {
        clientMap.set(clientKey, {
          key: clientKey,
          data: { type: "Folder", name: clientName, clientName },
          children: [],
        });
      }

      clientMap.get(clientKey).children.push({
        key: `${clientKey}-${yearKey}`,
        data: { type: "Folder", name: yearName },
        children: clientNode.children || [],
      });
    });
  });

  return Array.from(clientMap.values());
};

const handleSwitchHierarchy = () => {
  if (!originalHierarchy || originalHierarchy.length === 0) return;

  setSwitchingHierarchy(true);

  try {
    let processed;

    if (currentHierarchyMode === "default") {
      processed = swapYearAndClient(originalHierarchy);
      setCurrentHierarchyMode("client-first");
    } else {
      processed = originalHierarchy;
      setCurrentHierarchyMode("default");
    }

    const withLevels = addLevels(processed);
    setNodes(withLevels);
  } finally {
    setSwitchingHierarchy(false);
  }
};

const confirmRestore = async (reason) => {
  if (!contractToAction) return;
  setIsRestoring(true);
  startLoading();
  try {
    const payload = {
      user_id: userId,
      contract_id: parseInt(getCleanContractId(contractToAction.id)),
      reason: reason || 'moving from archive',
    };
    const response = await apiService.postData('enhanced-trash?is_deleted=false', payload);
    if (response.status === 'success' || response.status === 'Restored') {
      // Refetch deleted contracts to update the TreeTable
      const queryParams = `deleted-contracts?user_id=${userId}`;
      const fetchResponse = await apiService.fetchData(queryParams);
      const hierarchy = fetchResponse?.contract_hierarchy || [];
      const withLevels = addLevels(hierarchy);
      setNodes(withLevels);

      // Send email notification
      const emailData = {
        recipientName: userName || 'User',
        contractTitle: contractToAction.name,
        contractId: contractToAction.contract_id,
        previousStatus: contractToAction.status || 'Archived',
        userName: sessionStorage.getItem('user_name') || 'User',
        actionDate: new Date().toISOString().split('T')[0],
        actionReason: reason || 'moving from archive',
        contractUrl: contractToAction.file_url || '#',
        status: 'Active'
      };

      const html = RestoreEmailTemplate(emailData);

      try {
        await apiService.sendEmail(
          sessionStorage.getItem('user_emailId') || 'yasaswini.munnangi@criticalriver.com',
          'Contract Restored Successfully',
          html
        );
        console.log('Email sent successfully');
      } catch (emailError) {
        console.error('Failed to send email:', emailError);
        setSnackbarMessage('Contract restored, but email notification failed');
        setIsSuccess(false);
        setSnackbarOpen(true);
      }

      setSnackbar({
        open: true,
        isSuccess: true,
        message: `Contract (ID: ${getCleanContractId(contractToAction.id)}) restored successfully`,
      });
    } else {
      throw new Error(response.message || 'Failed to restore');
    }
  } catch (error) {
    console.error('Error restoring contract:', error);
    setSnackbar({
      open: true,
      isSuccess: false,
      message: `Failed to restore contract: ${error.message}`,
    });
  } finally {
    setIsRestoring(false);
    setIsRestoreModalOpen(false);
    setContractToAction(null);
    stopLoading();
  }
};

  
const toggleColumn = (key) => {
  if (mandatoryColumns.includes(key)) return;
  setTempVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
};

const applyColumnChanges = () => {
  const newVisible = { ...tempVisibleColumns };
  mandatoryColumns.forEach(col => {
    if (newVisible.hasOwnProperty(col)) newVisible[col] = true;
  });
  setVisibleColumns(newVisible);
  localStorage.setItem('trashTableVisibleColumns', JSON.stringify(newVisible));
  setShowColumnOptions(false);
};

const cancelColumnChanges = () => {
  setTempVisibleColumns({ ...visibleColumns });
  setShowColumnOptions(false);
};


  const handleOpenDocument = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  const sortedContracts = useMemo(() => {
    let sortableContracts = [...contracts];
    if (sortConfig.key) {
      sortableContracts.sort((a, b) => {
        let aValue = sortConfig.key === 'dateCreated' ? a.rawDate : a.rawModified;
        let bValue = sortConfig.key === 'dateCreated' ? b.rawDate : b.rawModified;
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableContracts;
  }, [contracts, sortConfig]);




  const filteredContracts = useMemo(() => {
    return sortedContracts.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.clientName.toLowerCase().includes(search.toLowerCase()) ||
        c.status.toLowerCase().includes(search.toLowerCase()) ||
        c.modified.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = !status || c.status === status;
      const matchesType = !type || c.tagFull === type;
      const matchesClient = !client || c.clientName === client;
      const matchesDays = !days || (() => {
        const trashedDate = c.rawModified;
        const now = new Date();
        const diffMs = now - trashedDate;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        return days === "all" || (diffMs >= 0 && diffDays <= parseInt(days));
      })();
      return matchesSearch && matchesStatus && matchesType && matchesClient && matchesDays;
    });
  }, [sortedContracts, search, status, type, client, days]);

  const totalContracts = filteredContracts.length;
  const totalPages = Math.max(1, Math.ceil(totalContracts / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalContracts);
  const currentContracts = useMemo(() => filteredContracts.slice(startIndex, endIndex), [filteredContracts, startIndex, endIndex]);

const buildExportRows = () => {
  const visibleKeys = Object.keys(visibleColumns).filter(
    (k) => visibleColumns[k] && k !== "actions"
  );
  const columnMap = {
    name: "Contract Name",
    clientName: "Client",
    dateCreated: "Created",
    status: "Status",
    modified: "Modified",
  };


  const files = filteredFiles.map((f) => ({
    name: f.contract_title || f.name || "Unnamed Contract",
    clientName: f.clientName || "N/A",
    dateCreated: f.createdDate || f.created_at || "N/A",
    status: f.status || "Trashed",
    modified: calculateModified(f.modified || f.updated_at || f.createdDate),
  }));

  const headers = visibleKeys.map((k) => columnMap[k] || k);
  const rows = files.map((f) => visibleKeys.map((k) => f[k] || ""));
  return { headers, rows };
};

const exportTableToCSV = () => {
  const { headers, rows } = buildExportRows();

 
  if (rows.length === 0) {
    setSnackbar({
      open: true,
      success: false,
      message: "No data available to export.",
    });
    setShowDownloadMenu(false);
    return;
  }

  const csvRows = [
    headers.join(","),
    ...rows.map((r) =>
      r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
    ),
  ];
  const blob = new Blob([csvRows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "trash_export.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setShowDownloadMenu(false);
};

const exportTablePDF = () => {
  const { headers, rows } = buildExportRows();

 
  if (rows.length === 0) {
    setSnackbar({
      open: true,
      success: false,
      message: "No data available to export.",
    });
    setShowDownloadMenu(false);
    return;
  }

  const doc = new jsPDF();
  autoTable(doc, {
    head: [headers],
    body: rows,
    theme: "grid",
    headStyles: { fillColor: "#ff6b35", textColor: "#ffffff" },
    styles: { fontSize: 10, cellPadding: 3 },
  });
  doc.save("trash_export.pdf");
  setShowDownloadMenu(false);
};




  return (
    <div className="trash-page-container">
      <Sidebar />
      <div className="trash-page-content">
      <Breadcrumbs /><br/>
      <div style={{ 
        background: '#fff', 
        borderRadius: '8px', 
        padding: '24px 28px', 
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)', 
        borderLeft: '4px solid #ff6b35', 
        marginBottom: '24px' 
      }}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          color: '#212529', 
          margin: 0, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px' 
        }}>
          <span style={{ 
            width: '28px', 
            height: '28px', 
            background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c61 100%)', 
            borderRadius: '6px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: '#fff', 
            fontSize: '16px' 
          }}>
            📋
          </span>
          Your Archived Contracts
        </h1>
      </div>
      
        <Loader 
          isLoading={isLoading} 
          message="Loading your Archives like a pro—almost there…" 
          title="Search by contract name, client or status"
        />
        {!isLoading && (
          <>
 {nodes && nodes.length > 0 && (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    
      {/* <h3 className="title">Archived Documents</h3> */}
      
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
      <button
  onClick={handleSwitchHierarchy}
  disabled={switchingHierarchy}
  style={{
    background: switchingHierarchy ? "#ccc" : "#ff6b35",
    color: "white",
    padding: "8px 14px",
    borderRadius: "6px",
    fontWeight: "bold",
    marginLeft: "10px",
    cursor: switchingHierarchy ? "not-allowed" : "pointer",
    border:'none',
  }}
>
  {switchingHierarchy ? "Switching..." : "Switch → " + (currentHierarchyMode === "default" ? "Client First" : "Year First")}
</button>
      <div style={{ position: 'relative', width: '170px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search documents"
          style={{
            padding: '8px 30px 8px 8px', 
            width: '100%', 
            border: '1px solid #ff6b35',
            borderRadius: '4px',
          }}
        />
        <FaSearch
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#4A4A4A',
          }}
        />
      </div>
      <button
        onClick={() => setShowColumnOptions(true)}
        style={{
          cursor: 'pointer',
          padding: '4px 12px',
          borderRadius: '6px',
          background: 'transparent',
          color: '#4A4A4A',
          border: '1px solid #ff6b35',
          fontSize: '16px',
          fontWeight: 500,
        }}
      >
        ⚙ Column options
      </button>
      <div style={{ position: 'relative' }} ref={downloadRef}>
        <button
          onClick={() => setShowDownloadMenu(!showDownloadMenu)}
          style={{
            cursor: 'pointer',
            padding: '4px 12px',
            borderRadius: '6px',
            background: 'transparent',
            border: '1px solid #ff6b35',
            color: '#4A4A4A',
            fontSize: '16px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        title="Click to export contracts as pdf or csv"
        >
          <img src={downloadIcon} alt="Download" style={{ width: '16px', height: '20px' }} /> Download
        </button>
        {showDownloadMenu && (
          <div
            className="Project-export-dropdown"
            style={{ position: 'absolute', right: 0, marginTop: '6px', borderRadius: '6px', background: 'white', border: '1px solid #e5e7eb', zIndex: 10 }}
          >
            <button
              onClick={exportTableToCSV}
              style={{
                cursor: 'pointer',
                border: 'none',
                display: 'block',
                padding: '8px 12px',
                width: '120px',
                textAlign: 'left',
                background: '#ff6b35',
                color: 'white',
              }}
            >
              Download CSV
            </button>
            <button
              onClick={exportTablePDF}
              style={{
                cursor: 'pointer',
                border: 'none',
                display: 'block',
                padding: '8px 12px',
                width: '120px',
                textAlign: 'left',
                background: 'none',
                color: '#4A4A4A',
              }}
            >
              Download PDF
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
)}
  <div style={{ overflow: "hidden" }}>
    <TreeTable
  value={filteredNodes}
  paginator
  rows={rowsPerPage || 10}
  rowsPerPageOptions={[5, 10, 20, 50]}
  expandedKeys={expandedKeys}
  onToggle={(e) => setExpandedKeys(e.value)}
  showGridlines
  tableStyle={{ width: "100%", tableLayout: "fixed" }}
  rowClassName={(node) => {
    if (node.data?.type === "Folder") return "folder-row"; 
    return "";
  }}
  
  emptyMessage={
  <div style={{
    textAlign: "center",
    padding: "40px 0",
    color: "#ff6b35",
    fontSize: "20px",
    fontWeight: 500,
    width: "100%"
  }}>
   Looks like your archive’s on vacation — no contracts here!
  </div>
}
>
  {visibleColumns.name && (
    <Column
      field="name"
      header={<span title="Contract Name">Contract Name</span>}
      style={{ width: "25%", color: "black" }}
      body={(node) => {
        const isFolder = node.data?.type === "Folder";
        const isExpanded = !!expandedKeys[node.key];
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              paddingLeft: `${node.level * 20}px`,
              color: "black",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {node.children && node.children.length > 0 && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedKeys((prev) => {
                    const newKeys = { ...prev };
                    if (newKeys[node.key]) {
                      delete newKeys[node.key];
                    } else {
                      newKeys[node.key] = true;
                    }
                    return newKeys;
                  });
                }}
                style={{
                  display: "inline-flex",
                  width: "18px",
                  height: "18px",
                  border: "1px solid black",
                  background: "white",
                  color: "#ff6b35",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  lineHeight: "18px",
                  verticalAlign: "middle",
                  fontSize: "18px",
                  marginLeft: "5px",
                }}
              >
                {isExpanded ? "−" : "+"}
              </span>
            )}
            {isFolder ? (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                📂 {node.data.name}
              </span>
            ) : (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                📄{" "}
                <a
                  href={node.data.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", textDecoration: "none" }}
                >
                  {node.data.name}
                </a>
              </span>
            )}
          </div>
        );
      }}
    />
  )}
  {visibleColumns.clientName && (
    <Column
      field="clientName"
      header={<span title="Client">Client</span>}
      style={{ width: "15%", textAlign: "center", color: "black" }}
      body={(node) => {
        if (node.level === 0) return null; // Hide for top-level (if client folders)
        return <span style={{ display: "block", textAlign: "center", color: "black", overflow: "hidden", textOverflow: "ellipsis" }}>{node.data.clientName}</span>;
      }}
    />
  )}
  {visibleColumns.dateCreated && (
    <Column
      field="createdDate"
      header={<span title="Created">Created</span>}
      style={{ width: "15%", textAlign: "center", color: "black" }}
      sortable
      body={(node) => {
        if (node.data?.type === "Folder") return null; // Hide for folders
        return <span style={{ display: "block", textAlign: "center", color: "black", overflow: "hidden", textOverflow: "ellipsis" }}>{node.data.createdDate}</span>;
      }}
    />
  )}
  {visibleColumns.status && (
    <Column
      field="status"
      header={<span title="Status">Status</span>}
      style={{ width: "15%", textAlign: "center", color: "black" }}
      body={(node) => {
        if (node.data?.type === "Folder") return null; // Hide for folders
        const status = node.data.status || "Trashed";
        const statusColorMap = {
          "Active": "#FF9800",
          "Approved": "#4CAF50",
          "Archived": "#9C27B0",
          "Draft": "#2196F3",
          "Expired": "#F44336",
          "Pending Approval": "#FFEB3B",
          "Signed": "#009688",
          "Terminated": "#616161",
          "Trashed": "#FF6F3C",
          "Moved to Trash": "#FF6F3C",
        };
        const backgroundStyle = {
          background: statusColorMap[status] || "#FF6F3C",
          padding: "4px 8px",
          borderRadius: "4px",
          color: status === "Pending Approval" ? "#333" : "#fff",
          display: "inline-block",
          textAlign: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        };
        return <span style={backgroundStyle}>{status}</span>;
      }}
    />
  )}
  {visibleColumns.modified && (
    <Column
      field="modified"
      header={<span title="Modified">Modified</span>}
      style={{ width: "15%", textAlign: "center", color: "black" }}
      sortable
      body={(node) => {
        if (node.data?.type === "Folder") return null; // Hide for folders
        return <span style={{ display: "block", textAlign: "center", color: "black", overflow: "hidden", textOverflow: "ellipsis" }}>{node.data.modified}</span>;
      }}
    />
  )}
  {visibleColumns.actions && (
    <Column
      header={<span title="Actions">Actions</span>}
      style={{ width: "15%", textAlign: "center", color: "black" }}
      body={(node) => {
        if (node.data?.type !== "File") return null; 
        const isFile = true;
        return (
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            {/* Info Icon */}
            <Info
              style={{ cursor: "pointer", color: "#ff6b35", width: "16px", height: "16px" }}
              titleAccess="View Details"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const cleanId = getCleanContractId(node.data.id);
                const clientKey = getClientKey(node.key);
                navigate("/categories", { state: { contractId: cleanId, clientKey, fromArchive: true, disableEdit: true } });
              }}
            />
            
            {/* Restore */}
            <RestoreIcon
              style={{ cursor: "pointer", color: "#ff6b35", width: "16px", height: "16px" }}
              titleAccess="Restore"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRestore(node.data);
              }}
            />
            
          </div>
        );
      }}
    />
  )}
</TreeTable>
  </div>

            {nodes && nodes.length > 0 && totalPages > 1 && (
  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
    <nav style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <button
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '18px',
          border: '1px solid #ff6b35',
          background: currentPage === 1 ? '#f3f4f6' : '#fff',
          color: currentPage === 1 ? '#9ca3af' : '#ff6b35',
          cursor: 'pointer',
        }}
      >
        {'<'}
      </button>
      {Array.from({ length: 3 }, (_, i) => {
        const startPage = Math.max(1, currentPage - 1);
        const page = startPage + i;
        if (page <= totalPages) {
          return (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                border: '1px solid #ff6b35',
                background: page === currentPage ? '#ff6b35' : '#fff',
                color: page === currentPage ? '#fff' : '#ff6b35',
                cursor: 'pointer',
              }}
            >
              {page}
            </button>
          );
        }
        return null;
      })}
      <button
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '18px',
          border: '1px solid #ff6b35',
          background: currentPage === totalPages ? '#f3f4f6' : '#fff',
          color: currentPage === totalPages ? '#9ca3af' : '#ff6b35',
          cursor: 'pointer',
        }}
      >
        {'>'}
      </button>
    </nav>
  </div>
)}
            {showColumnOptions && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(6px)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    width: '340px',
                    background: '#fff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginTop: '100px',
                    marginRight: '20px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 12px',
                      background: '#ff6b35',
                      color: '#fff',
                      fontWeight: 700,
                    }}
                  >
                    <div>Column Options</div>
                    <button
                      onClick={cancelColumnChanges}
                      aria-label="Close"
                      style={{ color: '#fff', background: 'transparent', border: 'none', fontSize: '25px', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </div>
                  <div
                    style={{
                      marginTop: '-8px',
                      fontSize: '12px',
                      color: '#6B7280',
                      padding: '0 12px 8px',
                      borderBottom: '1px solid #e5e7eb',
                    }}
                  >
                  </div>
                  <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: '10px 12px' }}>
                  
                    {['name', 'clientName', 'dateCreated', 'status', 'modified', 'actions'].map(
  (key) => (
    <div
      key={key}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid #f3f4f6',
      }}
    >
      <span style={{ fontSize: '14px', color: '#111827' }}>
        {key === 'name' ? 'Contract Name' : key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
        {mandatoryColumns.includes(key) ? <span style={{ color: '#ff6b35' }}> *</span> : ''}
      </span>
      <button
        onClick={() => toggleColumn(key)}
        disabled={mandatoryColumns.includes(key)}
        aria-label={tempVisibleColumns[key] ? `Hide ${key}` : `Show ${key}`}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: mandatoryColumns.includes(key) ? 'not-allowed' : 'pointer',
          opacity: mandatoryColumns.includes(key) ? 0.6 : 1
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#000000"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
          {!tempVisibleColumns[key] && (
            <line x1="2" y1="2" x2="18" y2="18" stroke="#000000" strokeWidth="3" />
          )}
        </svg>
      </button>
    </div>
  )
)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '10px 12px', borderTop: '1px solid #e5e7eb' }}>
                    <button
                      onClick={cancelColumnChanges}
                      style={{ background: 'transparent', color: '#ff6b35', border: '1px solid #ff6b35', padding: '8px 14px', borderRadius: '6px' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={applyColumnChanges}
                      style={{ background: '#ff6b35', color: '#fff', border: '1px solid #ff6b35', padding: '8px 14px', borderRadius: '6px' }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
           <RestoreConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={() => setIsRestoreModalOpen(false)}
        onConfirm={confirmRestore}
        contractName={contractToAction?.name || "Unnamed Document"}
        isRestoring={isRestoring}
      />
            <Snackbar
            open={snackbarOpen}
            autoHideDuration={5000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
            sx={{
               '& .MuiSnackbarContent-root': {
                backgroundColor: isSuccess ? '#4caf50' : '#f44336',
               },
               zIndex: 1400,
            }}
         />
          </>
        )}
      </div>
    </div>
  );
};

export default TrashPage;